// 页：流量分析（IDP spec §4.9 前端）
// 5 tab：总览 / 会话与路径 / 漏斗与留存 / 热区 / 实时事件流
// spec 锚点：
//   - 三层身份：device_id(_shopify_y 1y) / session_id(_shopify_s 30min) / customer_id
//   - sessionization 客户端切（30min 超时），engagement_time 客户端算
//   - 接口契约 POST /idp/events ≤50 events/请求；GET /idp/experiments/config 强缓存
//   - 写路径无回查无 UPDATE，维表 ID 解析 cache + ON DUPLICATE
//   - 数字化验收 4 项见数据健康页

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Tag, Select, Empty, Progress, Table, Timeline, Alert, Input, Button, Tooltip } from 'antd';
import { Tabs } from '../AntdCompat';
import { ClockCircleOutlined, EyeOutlined, ApiOutlined, DesktopOutlined, MobileOutlined, TabletOutlined, BulbOutlined } from '@ant-design/icons';
import RangeSelect from '@/components/RangeSelect';
import {
  idpEvents, idpPages, idpElements, idpSessionAggs, idpCohortRetentions,
  scrollDailyRollups, SITE_NAMES,
  getPathFlowFromPage, getExitPagesTopN,
  getEngagementDistribution, getDauWauMau, calcFunnel, getRealtimeEvents,
  getViewportBucketHeatmap, getScrollReadRate, getSessionEvents,
  buildSankeyData,
} from '../data';
import { EventType, DeviceType, FunnelStep, IdpEvent, IdpSessionAgg } from '../types';
import {
  getIdpFunnel,
  getIdpHeatmap,
  getIdpOverview,
  getIdpPathFlow,
  getIdpRealtime,
  getIdpSessions,
} from '@/services/idpAnalytics';
import {
  WEB_KEY_TO_SITE,
  webKeysForFilter,
} from '../siteWebKeys';
import { useIdpSiteAccess } from '../useIdpSiteAccess';
import type {
  IdpFunnelDTO,
  IdpHeatmapDTO,
  IdpOverviewDTO,
  IdpPathFlowDTO,
  IdpRealtimeEventDTO,
  IdpSessionDetailDTO,
  IdpSessionListDTO,
  IdpSessionSummaryDTO,
  IdpWebKey,
} from '@/services/idpAnalytics';

// 节点 pageType → 色卡（链路 + 节点共享）
const sankeyColorByType = (pt?: string) => {
  switch (pt) {
    case 'HOMEPAGE': return '#1890ff';
    case 'COLLECTION': return '#13c2c2';
    case 'PDP': return '#722ed1';
    case 'CART': return '#fa8c16';
    case 'CHECKOUT': return '#eb2f96';
    case 'BLOG': return '#52c41a';
    case 'ACTION': return '#fa541c';
    case 'EXIT': return '#8c8c8c';
    default: return '#bfbfbf';
  }
};

// 桑基图组件（纯手撸 SVG 不依赖 d3-sankey）
// 关键不变量（d3-sankey 标准）：
//   1. 全局统一 yScale = (availableH - 最多节点列的 gap) / max(每列 totalUsers)
//      → 所有列节点高度同 scale；不再每列各自 scale 致 ribbon 不对齐
//   2. ribbon 用 area path (fill)，上下两条贝塞尔曲线包住一个带状区域
//      → 不再用 stroke + strokeWidth；ribbon 宽 = link.value × yScale 严格守恒
//   3. 节点高度 = sum(incoming valueH) = sum(outgoing valueH)，肉眼可对账
const SankeyChart: React.FC<{
  data: ReturnType<typeof buildSankeyData>;
  width?: number;
  height?: number;
}> = ({ data, width = 900, height = 460 }) => {
  if (data.nodes.length === 0) {
    return <Empty description="无路径数据" />;
  }
  const PADDING_X = 20;
  const PADDING_Y = 10;
  const NODE_W = 14;
  const NODE_GAP = 4;

  const maxDepth = Math.max(...data.nodes.map(n => n.depth));
  const columnGap = maxDepth > 0 ? (width - PADDING_X * 2 - NODE_W) / maxDepth : 0;

  // 1. 分列 + 同列排序（EXIT 垫底，其他按 users desc）
  const columns: Array<Array<typeof data.nodes[0]>> = [];
  for (let d = 0; d <= maxDepth; d++) {
    const colNodes = data.nodes
      .filter(n => n.depth === d)
      .sort((a, b) => {
        if (a.pageType === 'EXIT' && b.pageType !== 'EXIT') return 1;
        if (b.pageType === 'EXIT' && a.pageType !== 'EXIT') return -1;
        return b.users - a.users;
      });
    columns.push(colNodes);
  }

  // 2. 全局统一 yScale
  //    取「总 users 最多的那一列」作为高度基准 — 其他列按同 scale 自然变矮居中显示
  const maxColumnTotalUsers = Math.max(...columns.map(c => c.reduce((s, n) => s + n.users, 0)), 1);
  const maxNodesInCol = Math.max(...columns.map(c => c.length), 1);
  const maxColumnGapPx = (maxNodesInCol - 1) * NODE_GAP;
  const availableH = height - PADDING_Y * 2;
  const yScale = maxColumnTotalUsers > 0
    ? (availableH - maxColumnGapPx) / maxColumnTotalUsers
    : 0;

  // 3. 节点 layout（同一 yScale，列内居中）
  const layout: Record<string, { x0: number; x1: number; y0: number; y1: number; node: typeof data.nodes[0] }> = {};
  for (let d = 0; d <= maxDepth; d++) {
    const colNodes = columns[d];
    const totalUsers = colNodes.reduce((s, n) => s + n.users, 0);
    const colHeight = totalUsers * yScale + (colNodes.length - 1) * NODE_GAP;
    let yCursor = PADDING_Y + (availableH - colHeight) / 2;
    const x0 = PADDING_X + d * columnGap;
    for (const n of colNodes) {
      const h = n.users * yScale;
      layout[n.id] = { x0, x1: x0 + NODE_W, y0: yCursor, y1: yCursor + h, node: n };
      yCursor += h + NODE_GAP;
    }
  }

  // 4. link area path —— 上下两条贝塞尔包围；宽度严格 = link.value × yScale
  //    sort by source.y0 然后 target.y0：让 ribbon 在 source/target 端按位置堆叠，减少交叉
  const linkPaths: Array<{
    d: string; color: string;
    sourceLabel: string; targetLabel: string; value: number;
  }> = [];
  const outCursor: Record<string, number> = {};
  const inCursor: Record<string, number> = {};
  const sortedLinks = data.links.slice().sort((a, b) => {
    const aSrcY = layout[a.source]?.y0 ?? 0;
    const bSrcY = layout[b.source]?.y0 ?? 0;
    if (aSrcY !== bSrcY) return aSrcY - bSrcY;
    const aTgtY = layout[a.target]?.y0 ?? 0;
    const bTgtY = layout[b.target]?.y0 ?? 0;
    return aTgtY - bTgtY;
  });

  for (const l of sortedLinks) {
    const src = layout[l.source];
    const tgt = layout[l.target];
    if (!src || !tgt) continue;
    const valueH = l.value * yScale;
    if (valueH < 0.5) continue; // 太细的 ribbon 不画（视觉噪声）

    const sy0 = src.y0 + (outCursor[l.source] || 0);
    const sy1 = sy0 + valueH;
    const ty0 = tgt.y0 + (inCursor[l.target] || 0);
    const ty1 = ty0 + valueH;
    outCursor[l.source] = (outCursor[l.source] || 0) + valueH;
    inCursor[l.target] = (inCursor[l.target] || 0) + valueH;

    const sx = src.x1;
    const tx = tgt.x0;
    const midX = (sx + tx) / 2;
    // 上沿贝塞尔 → tgt 端直线下到下沿 → 下沿反向贝塞尔回 src → 直线 close
    const path =
      `M${sx},${sy0} ` +
      `C${midX},${sy0} ${midX},${ty0} ${tx},${ty0} ` +
      `L${tx},${ty1} ` +
      `C${midX},${ty1} ${midX},${sy1} ${sx},${sy1} ` +
      `Z`;

    linkPaths.push({
      d: path,
      color: sankeyColorByType(tgt.node.pageType),
      sourceLabel: src.node.label,
      targetLabel: tgt.node.label,
      value: l.value,
    });
  }

  return (
    <svg width={width} height={height} style={{ background: '#fafafa', borderRadius: 4 }}>
      {linkPaths.map((l, i) => (
        <Tooltip
          key={i}
          title={<span>{l.sourceLabel} → {l.targetLabel}<br /><strong>{l.value}</strong> users</span>}
        >
          <path
            d={l.d}
            fill={l.color}
            fillOpacity={0.4}
            stroke="none"
            style={{ cursor: 'pointer', transition: 'fill-opacity 0.2s' }}
            onMouseEnter={e => (e.currentTarget.style.fillOpacity = '0.65')}
            onMouseLeave={e => (e.currentTarget.style.fillOpacity = '0.4')}
          />
        </Tooltip>
      ))}
      {Object.values(layout).map(({ x0, x1, y0, y1, node }) => (
        <g key={node.id}>
          <Tooltip title={<span>{node.label}<br />depth {node.depth} · {node.users} users</span>}>
            <rect
              x={x0}
              y={y0}
              width={x1 - x0}
              height={Math.max(2, y1 - y0)}
              fill={sankeyColorByType(node.pageType)}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
          {(y1 - y0) > 10 && (
            <text
              x={node.depth < maxDepth / 2 ? x1 + 4 : x0 - 4}
              y={(y0 + y1) / 2}
              dy={3}
              fontSize={10}
              fill="#262626"
              textAnchor={node.depth < maxDepth / 2 ? 'start' : 'end'}
              style={{ pointerEvents: 'none' }}
            >
              {node.label}<tspan fill="#8c8c8c"> ({node.users})</tspan>
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  page_view: '页面浏览', click: '点击', scroll: '滚动',
  add_to_cart: '加购', checkout_start: '结账开始', purchase: '购买', inquiry_open: '询单',
};
const EVENT_TYPE_COLORS: Record<EventType, string> = {
  page_view: 'blue', click: 'cyan', scroll: 'geekblue',
  add_to_cart: 'orange', checkout_start: 'gold', purchase: 'green', inquiry_open: 'magenta',
};

const DEVICE_ICONS: Record<DeviceType, React.ReactNode> = {
  mobile: <MobileOutlined />, desktop: <DesktopOutlined />, tablet: <TabletOutlined />,
};

// 默认 4 步漏斗
const DEFAULT_FUNNEL: FunnelStep[] = [
  { step_no: 1, name: '到访首页', match: { page_type: 'HOMEPAGE' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 2, name: '看商品 PDP', match: { page_type: 'PDP' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 3, name: '加入购物车', match: { event_type: 'add_to_cart' }, user_count: 0, drop_off_pct: 0 },
  { step_no: 4, name: '完成结账', match: { event_type: 'purchase' }, user_count: 0, drop_off_pct: 0 },
];

type ApiSessionSummary = IdpSessionSummaryDTO & { webKey: IdpWebKey };
type ApiSessionDetail = IdpSessionDetailDTO & { webKey: IdpWebKey };

type TrafficApiState = {
  loading: boolean;
  source: 'api' | 'mock';
  error?: string;
  overview?: IdpOverviewDTO;
  sessions: ApiSessionSummary[];
  detail?: ApiSessionDetail;
  funnel?: IdpFunnelDTO;
  heatmap?: IdpHeatmapDTO;
  realtime: IdpRealtimeEventDTO[];
  pathflow?: IdpPathFlowDTO;
};

const emptyApiState: TrafficApiState = {
  loading: false,
  source: 'mock',
  sessions: [],
  realtime: [],
};

type Settled<T> = { ok: true; value: T } | { ok: false; reason: any };

function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  return promise
    .then(value => ({ ok: true, value } as Settled<T>))
    .catch(reason => ({ ok: false, reason } as Settled<T>));
}

function errorMessage(reason: any) {
  return reason?.message || reason?.msg || String(reason || 'request failed');
}

function emptyOverview(webKey: IdpWebKey): IdpOverviewDTO {
  return {
    webKey,
    pv: 0,
    uv: 0,
    clicks: 0,
    dau: 0,
    wau: 0,
    mau: 0,
    trend: [],
    identityDistribution: [],
    deviceDistribution: [],
    eventTypeDistribution: [],
  };
}

function emptySessions(webKey: IdpWebKey): IdpSessionListDTO {
  return { webKey, sessions: [] };
}

function emptyFunnel(webKey: IdpWebKey): IdpFunnelDTO {
  return { webKey, steps: [] };
}

function toApiDateTime(date: Date) {
  return date.toISOString().slice(0, 19);
}

function normalizeDeviceType(value?: string): DeviceType {
  const v = String(value || '').toLowerCase();
  if (v.indexOf('desktop') > -1 || v === 'pc' || v === '0') return 'desktop';
  if (v.indexOf('tablet') > -1 || v.indexOf('pad') > -1) return 'tablet';
  return 'mobile';
}

function normalizeEventType(value?: string): EventType {
  const v = String(value || '').toLowerCase();
  const byServerName: Record<string, EventType> = {
    page_view: 'page_view',
    pageview: 'page_view',
    click: 'click',
    scroll: 'scroll',
    add_to_cart: 'add_to_cart',
    checkout_start: 'checkout_start',
    purchase: 'purchase',
    inquiry_open: 'inquiry_open',
    page_view_event: 'page_view',
    pageview_event: 'page_view',
  };
  return byServerName[v] || byServerName[v.replace(/-/g, '_')] || 'page_view';
}

function eventLabel(stepKey?: string) {
  const type = normalizeEventType(stepKey);
  return EVENT_TYPE_LABELS[type] || stepKey || '-';
}

function epochToIso(value?: number) {
  return value ? new Date(value).toISOString() : new Date().toISOString();
}

function sumDimension(points: Array<{ name?: string; value?: number }> | undefined, normalizer?: (name?: string) => string) {
  return (points || []).reduce((acc, item) => {
    const key = normalizer ? normalizer(item.name) : (item.name || 'unknown');
    acc[key] = (acc[key] || 0) + (item.value || 0);
    return acc;
  }, {} as Record<string, number>);
}

function aggregateOverview(list: IdpOverviewDTO[]): IdpOverviewDTO | undefined {
  if (!list.length) return undefined;
  const trendMap: Record<string, { day: string; pv: number; uv: number; clicks: number }> = {};
  const identity = {};
  const device = {};
  const eventTypes = {};

  list.forEach(item => {
    (item.trend || []).forEach(p => {
      if (!trendMap[p.day]) trendMap[p.day] = { day: p.day, pv: 0, uv: 0, clicks: 0 };
      trendMap[p.day].pv += p.pv || 0;
      trendMap[p.day].uv += p.uv || 0;
      trendMap[p.day].clicks += p.clicks || 0;
    });
    Object.assign(identity, sumObjects(identity, sumDimension(item.identityDistribution)));
    Object.assign(device, sumObjects(device, sumDimension(item.deviceDistribution, v => normalizeDeviceType(v))));
    Object.assign(eventTypes, sumObjects(eventTypes, sumDimension(item.eventTypeDistribution, v => normalizeEventType(v))));
  });

  return {
    webKey: list[0].webKey,
    pv: list.reduce((s, item) => s + (item.pv || 0), 0),
    uv: list.reduce((s, item) => s + (item.uv || 0), 0),
    clicks: list.reduce((s, item) => s + (item.clicks || 0), 0),
    dau: list.reduce((s, item) => s + (item.dau || 0), 0),
    wau: list.reduce((s, item) => s + (item.wau || 0), 0),
    mau: list.reduce((s, item) => s + (item.mau || 0), 0),
    trend: Object.values(trendMap).sort((a, b) => a.day.localeCompare(b.day)).slice(-14),
    identityDistribution: objectToDimension(identity),
    deviceDistribution: objectToDimension(device),
    eventTypeDistribution: objectToDimension(eventTypes),
  };
}

function sumObjects(a: Record<string, number>, b: Record<string, number>) {
  const next = { ...a };
  Object.keys(b).forEach(key => {
    next[key] = (next[key] || 0) + b[key];
  });
  return next;
}

function objectToDimension(obj: Record<string, number>) {
  return Object.keys(obj).map(name => ({ name, value: obj[name] }));
}

function mapApiEvent(event: IdpRealtimeEventDTO): IdpEvent {
  return {
    id: event.eventId || `${event.webKey}_${event.eventTime}_${event.sessionId}`,
    event_time: epochToIso(event.eventTime),
    event_type: normalizeEventType(event.eventType),
    device_id: event.deviceId || '',
    session_id: event.sessionId || '',
    customer_id: event.customerId,
    page_id: event.pageId,
    referrer: event.referrer,
    element_id: event.elementId,
    device_type: normalizeDeviceType(event.deviceType),
    scroll_depth: event.scrollDepth,
    engagement_ms: event.engagementMs,
    site_id: WEB_KEY_TO_SITE[event.webKey] ?? 2,
    props: {
      ...(event.props || {}),
      pageUrl: event.pageUrl,
      elementText: event.elementText,
    },
  };
}

function mapApiSession(summary: ApiSessionSummary, detail?: ApiSessionDetail): IdpSessionAgg {
  const events = detail?.sessionId === summary.sessionId ? (detail.events || []).map(mapApiEvent) : [];
  const pageEvents = events.filter(e => e.page_id);
  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];
  const firstPage = pageEvents[0];
  const lastPage = pageEvents[pageEvents.length - 1];
  const startedAt = summary.startedAt || detail?.startedAt || firstEvent?.event_time && new Date(firstEvent.event_time).getTime();
  const endedAt = summary.endedAt || detail?.endedAt || lastEvent?.event_time && new Date(lastEvent.event_time).getTime();

  return {
    session_id: summary.sessionId,
    device_id: summary.deviceId || detail?.deviceId || '',
    customer_id: summary.customerId || detail?.customerId,
    site_id: WEB_KEY_TO_SITE[summary.webKey] ?? 2,
    device_type: normalizeDeviceType(summary.deviceType || detail?.deviceType),
    start_at: epochToIso(startedAt || undefined),
    end_at: epochToIso(endedAt || undefined),
    duration_ms: summary.durationMs || detail?.durationMs || (startedAt && endedAt ? endedAt - startedAt : 0),
    page_view_count: events.length ? events.filter(e => e.event_type === 'page_view').length : 0,
    click_count: events.length ? events.filter(e => e.event_type === 'click').length : 0,
    page_path: pageEvents.map(e => ({
      page_id: e.page_id || 0,
      url: String(e.props?.pageUrl || summary.entryUrl || ''),
      title: String(e.props?.pageUrl || summary.entryUrl || `page#${e.page_id}`),
      arrived_at: e.event_time,
      engagement_ms: e.engagement_ms || 0,
    })),
    has_atc: events.some(e => e.event_type === 'add_to_cart'),
    has_purchase: events.some(e => e.event_type === 'purchase'),
    has_inquiry: events.some(e => e.event_type === 'inquiry_open'),
    exit_page_id: lastPage?.page_id || 0,
    exit_page_url: summary.exitUrl || detail?.exitUrl || String(lastPage?.props?.pageUrl || ''),
    entry_page_id: firstPage?.page_id || 0,
    entry_page_url: summary.entryUrl || detail?.entryUrl || String(firstPage?.props?.pageUrl || ''),
    event_count: summary.eventCount || detail?.eventCount || events.length || 0,
  };
}

function buildApiEngagementDistribution(events: IdpEvent[]) {
  const buckets = [
    { bucket: '<2s', min_ms: 0, max_ms: 2000, count: 0, pct: 0 },
    { bucket: '2-5s', min_ms: 2000, max_ms: 5000, count: 0, pct: 0 },
    { bucket: '5-10s', min_ms: 5000, max_ms: 10000, count: 0, pct: 0 },
    { bucket: '10-30s', min_ms: 10000, max_ms: 30000, count: 0, pct: 0 },
    { bucket: '30s-1m', min_ms: 30000, max_ms: 60000, count: 0, pct: 0 },
    { bucket: '>1m', min_ms: 60000, max_ms: Infinity, count: 0, pct: 0 },
  ];
  let total = 0;
  events.forEach(e => {
    if (!e.engagement_ms) return;
    total++;
    const bucket = buckets.find(b => e.engagement_ms! >= b.min_ms && e.engagement_ms! < b.max_ms);
    if (bucket) bucket.count++;
  });
  buckets.forEach(b => {
    b.pct = total > 0 ? b.count / total : 0;
  });
  return buckets;
}

const SpecCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="info"
    showIcon
    icon={<ApiOutlined />}
    message={<span style={{ fontSize: 12 }}>{children}</span>}
    style={{ marginBottom: 8 }}
  />
);
const BizCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="success"
    showIcon
    icon={<BulbOutlined />}
    message={<span style={{ fontSize: 12 }}><strong>业务方怎么用：</strong>{children}</span>}
    style={{ marginBottom: 12 }}
  />
);

// 真实 PDP 热区叠加 mock — 用珠宝主图 + 画 PDP 视觉布局 + 叠 SVG 红点 + hover 看 selector
// 生产部署改 iframe 真实 felicegals.com PDP + postMessage 探针，结构不变
const MOCK_PDP_IMG = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=800&q=80';
const PDP_HEATMAP_HOTSPOTS = [
  { x: 28, y: 28, selector: '.product-image-main', semantic: '商品主图', clicks_7d: 14523, pct: 28.4, intensity: 1.0 },
  { x: 70, y: 26, selector: '.add-to-cart-button', semantic: '加购按钮', clicks_7d: 8417, pct: 16.5, intensity: 0.85 },
  { x: 70, y: 17, selector: '.size-selector', semantic: '尺寸选择', clicks_7d: 5982, pct: 11.7, intensity: 0.65 },
  { x: 70, y: 22, selector: '.material-selector', semantic: '材质选择', clicks_7d: 4128, pct: 8.1, intensity: 0.55 },
  { x: 9, y: 55, selector: '.image-gallery-thumbnail:nth-child(1)', semantic: '缩略图 1', clicks_7d: 3892, pct: 7.6, intensity: 0.50 },
  { x: 9, y: 64, selector: '.image-gallery-thumbnail:nth-child(2)', semantic: '缩略图 2', clicks_7d: 2104, pct: 4.1, intensity: 0.42 },
  { x: 9, y: 73, selector: '.image-gallery-thumbnail:nth-child(3)', semantic: '缩略图 3', clicks_7d: 1248, pct: 2.4, intensity: 0.32 },
  { x: 36, y: 72, selector: '.product-description-expand', semantic: '描述展开', clicks_7d: 3284, pct: 6.4, intensity: 0.48 },
  { x: 70, y: 36, selector: '.wishlist-button', semantic: '收藏按钮', clicks_7d: 1854, pct: 3.6, intensity: 0.40 },
  { x: 70, y: 42, selector: '.size-guide-link', semantic: '尺码引导', clicks_7d: 1124, pct: 2.2, intensity: 0.30 },
  { x: 70, y: 50, selector: '.shipping-info', semantic: '物流说明', clicks_7d: 892, pct: 1.7, intensity: 0.28 },
  { x: 38, y: 88, selector: '.related-products .item:first-child', semantic: '相关商品 1', clicks_7d: 1542, pct: 3.0, intensity: 0.38 },
];
const PdpHeatmapMock: React.FC = () => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const totalClicks = PDP_HEATMAP_HOTSPOTS.reduce((s, h) => s + h.clicks_7d, 0);
  const hovered = hoveredIdx !== null ? PDP_HEATMAP_HOTSPOTS[hoveredIdx] : null;

  return (
    <div>
      <div style={{
        position: 'relative', width: '100%', height: 580,
        background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden',
      }}>
        {/* PDP 视觉框架 — 用 div 画 */}
        {/* 顶部 nav */}
        <div style={{ height: 36, background: '#1a1a1a', display: 'flex', alignItems: 'center', padding: '0 12px', color: '#fff', fontSize: 11 }}>
          <strong style={{ fontSize: 14 }}>FELICEGALS</strong>
          <span style={{ marginLeft: 16, opacity: 0.7 }}>Rings · Necklaces · Earrings · Sale</span>
          <span style={{ marginLeft: 'auto' }}>🔍 ❤️ 🛒 (2)</span>
        </div>
        <div style={{ position: 'relative', height: 'calc(100% - 36px)' }}>
          {/* 左：缩略图 */}
          <div style={{ position: 'absolute', left: '2%', top: '8%', width: '6%', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ width: '100%', aspectRatio: '1/1', background: `url(${MOCK_PDP_IMG})`, backgroundSize: 'cover', borderRadius: 3, border: i === 0 ? '2px solid #1890ff' : '1px solid #d9d9d9' }} />
            ))}
          </div>
          {/* 主图 */}
          <div style={{ position: 'absolute', left: '12%', top: '4%', width: '40%', height: '54%', background: `url(${MOCK_PDP_IMG})`, backgroundSize: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} />
          {/* 右：产品信息 */}
          <div style={{ position: 'absolute', left: '57%', top: '4%', width: '40%' }}>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>FELICEGALS / GOTHIC RINGS</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>Gothic Vintage Cathedral Ring</div>
            <div style={{ fontSize: 11, color: '#fa8c16', marginTop: 4 }}>⭐⭐⭐⭐⭐ 4.8 (124)</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 12, color: '#262626' }}>$ 198.00</div>
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>Free shipping over $99</div>
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600 }}>SIZE</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {['5', '6', '7', '8', '9'].map(s => (
                  <div key={s} style={{ width: 30, height: 30, border: '1px solid #d9d9d9', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>{s}</div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600 }}>MATERIAL</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {['14K Gold', 'Sterling Silver', 'Rose Gold'].map(m => (
                  <div key={m} style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 10 }}>{m}</div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 16, padding: 12, background: '#1a1a1a', color: '#fff', textAlign: 'center', borderRadius: 3, fontWeight: 600 }}>
              ADD TO CART
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: '#595959' }}>
              <span>♡ Wishlist</span>
              <span>📏 Size Guide</span>
              <span>🚚 Shipping</span>
            </div>
          </div>
          {/* 描述区 */}
          <div style={{ position: 'absolute', left: '12%', top: '62%', width: '40%' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>DESCRIPTION</div>
            <div style={{ fontSize: 10, color: '#595959', lineHeight: 1.6 }}>
              Handcrafted Gothic style ring inspired by cathedral architecture, with intricate spire details... <span style={{ color: '#1890ff', cursor: 'pointer' }}>Read more ▼</span>
            </div>
          </div>
          {/* 相关商品 */}
          <div style={{ position: 'absolute', left: '12%', bottom: '4%', width: '85%' }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>YOU MAY ALSO LIKE</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} style={{ width: 60, height: 60, background: `url(${MOCK_PDP_IMG})`, backgroundSize: 'cover', borderRadius: 3, border: '1px solid #f0f0f0' }} />
              ))}
            </div>
          </div>

          {/* SVG 热点叠加 */}
          <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            {/* 热区色斑 */}
            {PDP_HEATMAP_HOTSPOTS.map((h, i) => (
              <radialGradient key={`grad_${i}`} id={`hotspot_grad_${i}`}>
                <stop offset="0%" stopColor={`rgba(255, 0, 0, ${h.intensity * 0.6})`} />
                <stop offset="50%" stopColor={`rgba(255, 200, 0, ${h.intensity * 0.25})`} />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
            ))}
            {PDP_HEATMAP_HOTSPOTS.map((h, i) => (
              <ellipse
                key={`hot_${i}`}
                cx={`${h.x}%`}
                cy={`${h.y}%`}
                rx={30 + h.intensity * 35}
                ry={20 + h.intensity * 25}
                fill={`url(#hotspot_grad_${i})`}
              />
            ))}
            {/* 中心点 + hover */}
            {PDP_HEATMAP_HOTSPOTS.map((h, i) => (
              <circle
                key={`dot_${i}`}
                cx={`${h.x}%`}
                cy={`${h.y}%`}
                r={4 + h.intensity * 3}
                fill={hoveredIdx === i ? '#cf1322' : 'rgba(207, 19, 34, 0.8)'}
                stroke="#fff"
                strokeWidth={1.5}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              />
            ))}
            {/* 高亮选中元素 outline */}
            {hovered && (
              <rect
                x={`${hovered.x - 8}%`}
                y={`${hovered.y - 5}%`}
                width="16%"
                height="10%"
                fill="none"
                stroke="#cf1322"
                strokeWidth={2}
                strokeDasharray="4 2"
                rx={2}
                style={{ pointerEvents: 'none' }}
              />
            )}
          </svg>
        </div>
      </div>

      {/* hover 详情面板 */}
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
          {hovered ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong style={{ fontSize: 13 }}>{hovered.semantic}</strong>
                <span style={{ color: '#cf1322', fontWeight: 600 }}>{hovered.clicks_7d.toLocaleString()} 点击 / 7d</span>
              </div>
              <div style={{ marginTop: 4 }}>
                selector: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 2 }}>{hovered.selector}</code>
              </div>
              <div style={{ marginTop: 4, color: '#8c8c8c' }}>
                占 PDP 总点击 <strong>{hovered.pct}%</strong> · 位置 ({hovered.x}%, {hovered.y}%)
              </div>
            </>
          ) : (
            <div style={{ color: '#8c8c8c' }}>🖱️ hover 热点查看 selector + 7d 点击数 + 占比 + 位置坐标</div>
          )}
        </div>
        <div style={{ width: 200, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11, textAlign: 'center' }}>
          <div style={{ color: '#8c8c8c' }}>累计 7d</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#cf1322' }}>{totalClicks.toLocaleString()}</div>
          <div style={{ color: '#8c8c8c' }}>click events</div>
        </div>
      </div>
    </div>
  );
};

type HeatmapOverlayPoint = {
  x: number;
  y: number;
  intensity: number;
  selector?: string;
  elementText?: string;
  clicks?: number;
  uniqueUsers?: number;
};

type ProbeElement = {
  selector: string;
  found: boolean;
  text?: string;
  rect?: {
    xPct?: number;
    yPct?: number;
    width?: number;
    height?: number;
  };
};

function originOf(url?: string) {
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch (e) {
    return '';
  }
}

const IframeHeatmapProbe: React.FC<{
  pageUrl: string;
  points: HeatmapOverlayPoint[];
}> = ({ pageUrl, points }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const requestIdRef = useRef(0);
  const [status, setStatus] = useState<'idle' | 'waiting' | 'ready'>('idle');
  const [probeElements, setProbeElements] = useState<Record<string, ProbeElement>>({});

  const selectors = useMemo(() => (
    Array.from(new Set(points.map(point => point.selector).filter(Boolean) as string[])).slice(0, 100)
  ), [points.map(point => point.selector || '').join('|')]);

  const targetOrigin = originOf(pageUrl);

  const sendProbe = () => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow || !targetOrigin || !selectors.length) return;
    requestIdRef.current += 1;
    setStatus('waiting');
    frame.contentWindow.postMessage({
      source: 'idp-admin-heatmap',
      type: 'IDP_HEATMAP_INSPECT',
      requestId: `heatmap-${requestIdRef.current}`,
      selectors,
      highlight: false,
    }, targetOrigin);
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      if (event.origin !== targetOrigin) return;
      const data = event.data || {};
      if (data.source !== 'idp-events-sdk' || data.type !== 'IDP_HEATMAP_INSPECT_RESULT') return;
      const next: Record<string, ProbeElement> = {};
      (data.elements || []).forEach((item: ProbeElement) => {
        if (item?.selector) next[item.selector] = item;
      });
      setProbeElements(next);
      setStatus('ready');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [targetOrigin]);

  useEffect(() => {
    const timer = window.setTimeout(sendProbe, 600);
    return () => window.clearTimeout(timer);
  }, [pageUrl, selectors.join('|')]);

  const overlayPoints = points.map(point => {
    const probe = point.selector ? probeElements[point.selector] : undefined;
    if (probe?.found && probe.rect?.xPct !== undefined && probe.rect?.yPct !== undefined) {
      return {
        ...point,
        x: probe.rect.xPct,
        y: probe.rect.yPct,
        elementText: probe.text || point.elementText,
        probed: true,
      };
    }
    return { ...point, probed: false };
  });
  const totalClicks = points.reduce((sum, item) => sum + (item.clicks || 0), 0);
  const foundCount = Object.values(probeElements).filter(item => item.found).length;

  return (
    <div>
      <div style={{
        position: 'relative',
        width: '100%',
        height: 580,
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <iframe
          ref={iframeRef}
          src={pageUrl}
          title="IDP heatmap page preview"
          onLoad={sendProbe}
          style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
        />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {overlayPoints.map((point, index) => {
            const size = 42 + point.intensity * 54;
            return (
              <Tooltip
                key={`${point.selector || index}_${index}`}
                title={<span>
                  {point.elementText || point.selector || 'unknown'}
                  <br />
                  {point.clicks || 0} clicks / {point.uniqueUsers || 0} users
                  <br />
                  {point.selector}
                </span>}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: `${Math.max(0, Math.min(100, point.x))}%`,
                    top: `${Math.max(0, Math.min(100, point.y))}%`,
                    width: size,
                    height: size,
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, rgba(207,19,34,${0.55 * point.intensity}) 0%, rgba(250,140,22,${0.28 * point.intensity}) 45%, rgba(250,140,22,0) 72%)`,
                    border: point.probed ? '1px solid rgba(207,19,34,0.8)' : '1px dashed rgba(207,19,34,0.45)',
                    pointerEvents: 'auto',
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
          <strong>{targetOrigin || pageUrl}</strong>
          <span style={{ marginLeft: 8, color: '#8c8c8c' }}>
            {status === 'ready' ? `SDK 探针命中 ${foundCount}/${selectors.length} 个 selector` : `等待 SDK 探针返回，当前按 click 坐标叠加 ${overlayPoints.length} 个热点`}
          </span>
        </div>
        <div style={{ width: 200, padding: 10, background: '#f5f5f5', borderRadius: 4, fontSize: 11, textAlign: 'center' }}>
          <div style={{ color: '#8c8c8c' }}>当前页面</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#cf1322' }}>{totalClicks.toLocaleString()}</div>
          <div style={{ color: '#8c8c8c' }}>click events</div>
        </div>
      </div>
    </div>
  );
};

type SessionFilters = {
  keyword: string;            // device_id/customer_id/session_id 模糊
  entryPageId: number | 'all';
  exitPageId: number | 'all';
  conversions: Array<'atc' | 'purchase' | 'inquiry' | 'bounce'>;
  durationBuckets: Array<'lt30s' | '30s_5m' | '5m_30m' | 'gt30m'>;
  deviceTypes: DeviceType[];
};

type TrafficAnalysisProps = {
  initialSite?: string;
};

const TrafficAnalysis: React.FC<TrafficAnalysisProps> = ({ initialSite = 'all' }) => {
  const {
    filterSite,
    setFilterSite,
    allowedSiteFilters,
    siteOptions,
    loading: siteAccessLoading,
    error: siteAccessError,
    hasAccess: hasSiteAccess,
  } = useIdpSiteAccess(initialSite);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(idpSessionAggs[0]?.session_id || null);
  const [selectedFlowPageId, setSelectedFlowPageId] = useState<number>(1);
  const [funnelSteps, setFunnelSteps] = useState<FunnelStep[]>(DEFAULT_FUNNEL);
  const [sankeyStart, setSankeyStart] = useState<string>('any'); // 'any' | 'type:HOMEPAGE' | 'page:5'
  const [sankeyMaxDepth, setSankeyMaxDepth] = useState<number>(5);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [apiState, setApiState] = useState<TrafficApiState>(emptyApiState);
  const [apiRefreshKey, setApiRefreshKey] = useState(0);
  // 时间区间（默认近 7 天）；RangeSelect 回吐毫秒时间戳
  const [dateRange, setDateRange] = useState<{ from: number; to: number }>(() => {
    const to = Date.now();
    return { from: to - 7 * 24 * 3600 * 1000, to };
  });

  // 单 session 查询条件
  const [sessionFilters, setSessionFilters] = useState<SessionFilters>({
    keyword: '',
    entryPageId: 'all',
    exitPageId: 'all',
    conversions: [],
    durationBuckets: [],
    deviceTypes: [],
  });
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  useEffect(() => {
    if (siteAccessLoading || !filterSite) return;
    const params = new URLSearchParams(window.location.search);
    const selectedWebKeys = webKeysForFilter(filterSite, allowedSiteFilters);
    if (filterSite === 'all' || selectedWebKeys.length !== 1) {
      params.delete('webKey');
    } else {
      params.set('webKey', selectedWebKeys[0]);
    }
    const search = params.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [filterSite, siteAccessLoading, allowedSiteFilters.join(',')]);

  useEffect(() => {
    let cancelled = false;
    if (siteAccessLoading) {
      setApiState(prev => ({
        ...prev,
        loading: true,
        error: undefined,
      }));
      return () => {
        cancelled = true;
      };
    }
    if (!hasSiteAccess) {
      setApiState({
        ...emptyApiState,
        error: siteAccessError || '当前员工没有 FG/ST/BF 独立站渠道权限',
      });
      return () => {
        cancelled = true;
      };
    }
    const to = new Date(dateRange.to);
    const from = new Date(dateRange.from);
    const baseParams = {
      from: toApiDateTime(from),
      to: toApiDateTime(to),
    };
    const webKeys = webKeysForFilter(filterSite, allowedSiteFilters);
    const primaryWebKey = webKeys[0] || 'BF';

    setApiState(prev => ({
      ...prev,
      loading: true,
      error: undefined,
    }));

    Promise.all(webKeys.map(async webKey => {
      const errors: string[] = [];
      const [overviewResult, sessionsResult, funnelResult, realtimeResult] = await Promise.all([
        settle(getIdpOverview({ webKey, ...baseParams })),
        settle(getIdpSessions({ webKey, ...baseParams, sessionId: selectedSessionId || undefined })),
        settle(getIdpFunnel({
          webKey,
          ...baseParams,
          steps: ['PAGE_VIEW', 'ADD_TO_CART', 'CHECKOUT_START', 'PURCHASE'],
        })),
        settle(getIdpRealtime({ webKey, limit: Math.max(20, Math.floor(60 / Math.max(webKeys.length, 1))) })),
      ]);
      if (!overviewResult.ok) errors.push(`${webKey} overview: ${errorMessage(overviewResult.reason)}`);
      if (!sessionsResult.ok) errors.push(`${webKey} sessions: ${errorMessage(sessionsResult.reason)}`);
      if (!funnelResult.ok) errors.push(`${webKey} funnel: ${errorMessage(funnelResult.reason)}`);
      if (!realtimeResult.ok) errors.push(`${webKey} realtime: ${errorMessage(realtimeResult.reason)}`);
      return {
        webKey,
        overview: overviewResult.ok ? overviewResult.value : emptyOverview(webKey),
        sessions: sessionsResult.ok ? sessionsResult.value : emptySessions(webKey),
        funnel: funnelResult.ok ? funnelResult.value : emptyFunnel(webKey),
        realtime: realtimeResult.ok ? realtimeResult.value : [],
        errors,
      };
    }))
      .then(async parts => {
        const apiErrors = parts.reduce((acc, part) => acc.concat(part.errors), [] as string[]);
        const heatmap = await getIdpHeatmap({
          webKey: primaryWebKey,
          pageId: selectedFlowPageId,
          deviceBucket: 'all',
          ...baseParams,
        }).catch(error => {
          apiErrors.push(`${primaryWebKey} heatmap: ${errorMessage(error)}`);
          return undefined;
        });
        const pathflow = await getIdpPathFlow({
          webKey: primaryWebKey,
          ...baseParams,
        }).catch(error => {
          apiErrors.push(`${primaryWebKey} pathflow: ${errorMessage(error)}`);
          return undefined;
        });

        if (cancelled) return;

        const sessions: ApiSessionSummary[] = [];
        let detail: ApiSessionDetail | undefined;
        parts.forEach(part => {
          (part.sessions.sessions || []).forEach(s => sessions.push({ ...s, webKey: part.webKey }));
          if (part.sessions.detail?.sessionId) {
            detail = { ...part.sessions.detail, webKey: part.webKey };
          }
        });

        setApiState({
          loading: false,
          source: 'api',
          overview: aggregateOverview(parts.map(p => p.overview).filter(Boolean)),
          sessions,
          detail,
          funnel: parts.find(part => part.funnel?.steps?.length)?.funnel || parts[0]?.funnel,
          heatmap,
          realtime: parts.reduce((acc, part) => acc.concat(part.realtime || []), [] as IdpRealtimeEventDTO[]),
          pathflow,
          error: apiErrors.length ? `部分 IDP API 失败：${apiErrors.slice(0, 3).join('；')}` : undefined,
        });
      })
      .catch(error => {
        if (cancelled) return;
        setApiState({
          ...emptyApiState,
          error: error?.message || 'IDP analytics API unavailable',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [filterSite, selectedSessionId, selectedFlowPageId, apiRefreshKey, dateRange.from, dateRange.to, siteAccessLoading, hasSiteAccess, siteAccessError, allowedSiteFilters.join(',')]);

  useEffect(() => {
    if (activeTab !== 'realtime') return undefined;
    const timer = window.setInterval(() => {
      setApiRefreshKey(v => v + 1);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [activeTab]);

  const usingApi = apiState.source === 'api';

  const apiEvents = useMemo(() => (apiState.realtime || []).map(mapApiEvent), [apiState.realtime]);

  const allSessions = useMemo(() => {
    if (usingApi) {
      return (apiState.sessions || []).map(s => mapApiSession(s, apiState.detail));
    }
    if (filterSite === 'all') return idpSessionAggs;
    return idpSessionAggs.filter(s => String(s.site_id) === filterSite);
  }, [apiState.sessions, apiState.detail, filterSite, usingApi]);

  useEffect(() => {
    if (!allSessions.length) return;
    if (!selectedSessionId || !allSessions.some(s => s.session_id === selectedSessionId)) {
      setSelectedSessionId(allSessions[0].session_id);
    }
  }, [allSessions, selectedSessionId]);

  const filteredEvents = useMemo(() => {
    if (usingApi) return apiEvents;
    if (filterSite === 'all') return idpEvents;
    return idpEvents.filter(e => String(e.site_id) === filterSite);
  }, [apiEvents, filterSite, usingApi]);

  // 站点 + 单 session 查询条件过滤
  const filteredSessions = useMemo(() => {
    return allSessions.filter(s => {
      // 关键词:模糊匹配 device_id / customer_id / session_id
      if (sessionFilters.keyword) {
        const kw = sessionFilters.keyword.toLowerCase();
        const hit = s.device_id.toLowerCase().includes(kw)
          || (s.customer_id?.toLowerCase().includes(kw) ?? false)
          || s.session_id.toLowerCase().includes(kw);
        if (!hit) return false;
      }
      if (sessionFilters.entryPageId !== 'all' && s.entry_page_id !== sessionFilters.entryPageId) return false;
      if (sessionFilters.exitPageId !== 'all' && s.exit_page_id !== sessionFilters.exitPageId) return false;
      if (sessionFilters.deviceTypes.length > 0 && !sessionFilters.deviceTypes.includes(s.device_type)) return false;
      // 转化筛(任一命中即可)
      if (sessionFilters.conversions.length > 0) {
        const matchConversion = sessionFilters.conversions.some(c => {
          if (c === 'atc') return s.has_atc;
          if (c === 'purchase') return s.has_purchase;
          if (c === 'inquiry') return s.has_inquiry;
          if (c === 'bounce') return s.page_view_count <= 1;
          return false;
        });
        if (!matchConversion) return false;
      }
      // 时长区间(任一命中)
      if (sessionFilters.durationBuckets.length > 0) {
        const sec = s.duration_ms / 1000;
        const matchDuration = sessionFilters.durationBuckets.some(b => {
          if (b === 'lt30s') return sec < 30;
          if (b === '30s_5m') return sec >= 30 && sec < 300;
          if (b === '5m_30m') return sec >= 300 && sec < 1800;
          if (b === 'gt30m') return sec >= 1800;
          return false;
        });
        if (!matchDuration) return false;
      }
      return true;
    });
  }, [allSessions, sessionFilters]);

  const resetSessionFilters = () => setSessionFilters({
    keyword: '', entryPageId: 'all', exitPageId: 'all',
    conversions: [], durationBuckets: [], deviceTypes: [],
  });
  const activeFilterCount =
    (sessionFilters.keyword ? 1 : 0) +
    (sessionFilters.entryPageId !== 'all' ? 1 : 0) +
    (sessionFilters.exitPageId !== 'all' ? 1 : 0) +
    sessionFilters.conversions.length +
    sessionFilters.durationBuckets.length +
    sessionFilters.deviceTypes.length;

  // 三层身份计数
  const apiIdentity = sumDimension(apiState.overview?.identityDistribution);
  const uniqueDevices = usingApi ? (apiState.overview?.uv || 0) : new Set(filteredEvents.map(e => e.device_id)).size;
  const uniqueSessions = usingApi ? allSessions.length : new Set(filteredEvents.map(e => e.session_id)).size;
  const uniqueCustomers = usingApi ? (apiIdentity.customer || 0) : new Set(filteredEvents.filter(e => e.customer_id).map(e => e.customer_id)).size;
  const customerCoverage = uniqueDevices > 0 ? uniqueCustomers / uniqueDevices : 0;

  // 设备分布
  const deviceStats = useMemo(() => {
    const m: Record<DeviceType, number> = { mobile: 0, desktop: 0, tablet: 0 };
    if (usingApi && apiState.overview?.deviceDistribution?.length) {
      apiState.overview.deviceDistribution.forEach(item => {
        const key = normalizeDeviceType(item.name);
        m[key] += item.value || 0;
      });
      return m;
    }
    filteredSessions.forEach(s => m[s.device_type]++);
    return m;
  }, [apiState.overview, filteredSessions, usingApi]);

  // 事件类型分布
  const eventTypeStats = useMemo(() => {
    const map: Partial<Record<EventType, number>> = {};
    if (usingApi && apiState.overview?.eventTypeDistribution?.length) {
      apiState.overview.eventTypeDistribution.forEach(item => {
        const key = normalizeEventType(item.name);
        map[key] = (map[key] || 0) + (item.value || 0);
      });
      return map;
    }
    filteredEvents.forEach(e => { map[e.event_type] = (map[e.event_type] || 0) + 1; });
    return map;
  }, [apiState.overview, filteredEvents, usingApi]);
  const eventTypeTotal = Math.max(Object.values(eventTypeStats).reduce((s, n) => s + (n || 0), 0), 1);

  // DAU/WAU/MAU
  const dauWauMau = useMemo(() => {
    if (usingApi && apiState.overview?.trend?.length) {
      return {
        dau: apiState.overview.trend.map(d => ({ date: d.day, users: d.uv || 0 })),
        wau: apiState.overview.wau || 0,
        mau: apiState.overview.mau || 0,
      };
    }
    return getDauWauMau();
  }, [apiState.overview, usingApi]);

  // 选中的 session 详情
  const selectedSession = allSessions.find(s => s.session_id === selectedSessionId);
  const selectedSessionEvents = useMemo(() => {
    if (usingApi && apiState.detail?.sessionId === selectedSessionId) {
      return (apiState.detail.events || []).map(mapApiEvent);
    }
    return selectedSessionId ? getSessionEvents(selectedSessionId) : [];
  }, [apiState.detail, selectedSessionId, usingApi]);

  // 路径分析（从某页出发）
  const pathFlow = useMemo(() => getPathFlowFromPage(selectedFlowPageId), [selectedFlowPageId]);
  const exitPages = useMemo(() => getExitPagesTopN(8), []);

  // 漏斗（直线版,保留作 fallback）
  const calculatedFunnel = useMemo(() => {
    if (usingApi && apiState.funnel?.steps?.length) {
      return apiState.funnel.steps.map((s, i) => ({
        step_no: s.stepIndex || i + 1,
        name: eventLabel(s.stepKey || s.label),
        match: { event_type: normalizeEventType(s.stepKey || s.label) },
        user_count: s.users || 0,
        drop_off_pct: s.dropOffRate || 0,
      }));
    }
    return calcFunnel(funnelSteps);
  }, [apiState.funnel, funnelSteps, usingApi]);
  const funnelMax = calculatedFunnel[0]?.user_count || 1;

  // 桑基图数据
  const sankeyData = useMemo(() => {
    if (usingApi && apiState.pathflow?.sankeyNodes?.length) {
      // 后端 pathFlow 已做 per-session 精确分层（depth+pageType+EXIT+action 节点），直接用
      return {
        nodes: apiState.pathflow.sankeyNodes,
        links: apiState.pathflow.sankeyLinks || [],
        totalStart: apiState.pathflow.sankeyTotalStart || 0,
      };
    }
    const opts: Parameters<typeof buildSankeyData>[0] = { maxDepth: sankeyMaxDepth };
    if (sankeyStart.startsWith('page:')) {
      opts.startPageId = parseInt(sankeyStart.slice(5), 10);
    } else if (sankeyStart.startsWith('type:')) {
      opts.startPageType = sankeyStart.slice(5);
    } // else 'any' → 不传起点
    return buildSankeyData(opts);
  }, [sankeyStart, sankeyMaxDepth, usingApi, apiState.pathflow]);

  // Cohort
  const cohorts = idpCohortRetentions;

  // 热区
  const viewportHeatmap = useMemo(() => {
    if (usingApi && apiState.heatmap?.clicks?.length) {
      const maxClicks = Math.max(...apiState.heatmap.clicks.map(h => h.clicks || 0), 1);
      const points = apiState.heatmap.clicks
        .filter(h => h.xPct !== undefined && h.yPct !== undefined)
        .map(h => ({
          x: h.xPct || 0,
          y: h.yPct || 0,
          intensity: Math.max(0.15, (h.clicks || 0) / maxClicks),
          selector: h.elementSelector,
          elementText: h.elementText,
          clicks: h.clicks || 0,
          uniqueUsers: h.uniqueUsers || 0,
        }));
      return { mobile: points, desktop: points, tablet: [] };
    }
    return getViewportBucketHeatmap();
  }, [apiState.heatmap, usingApi]);
  const scrollReadRate = useMemo(() => {
    if (usingApi && apiState.heatmap?.scrollBuckets?.length) {
      const total = apiState.heatmap.scrollBuckets.reduce((s, b) => s + (b.users || 0), 0) || 1;
      const reached100 = apiState.heatmap.scrollBuckets.find(b => b.bucket >= 100)?.users || 0;
      const topBounce = apiState.heatmap.scrollBuckets.find(b => b.bucket <= 25)?.users || 0;
      return [{
        page_id: apiState.heatmap.pageId,
        title: apiState.heatmap.pageUrl || `page#${apiState.heatmap.pageId}`,
        url: apiState.heatmap.pageUrl || '',
        reached_100_pct: reached100 / total,
        topBounce_pct: topBounce / total,
      }];
    }
    return getScrollReadRate();
  }, [apiState.heatmap, usingApi]);

  // 实时
  const realtimeEvents = useMemo(() => usingApi ? apiEvents : getRealtimeEvents(60), [apiEvents, usingApi]);
  const engagementDist = useMemo(() => usingApi ? buildApiEngagementDistribution(apiEvents) : getEngagementDistribution(), [apiEvents, usingApi]);

  // 单 session timeline
  const sessionTimelineColor = (et: EventType) => ({
    page_view: 'blue', click: 'cyan', scroll: '#bfbfbf',
    add_to_cart: 'orange', checkout_start: 'gold', purchase: 'green', inquiry_open: 'magenta',
  } as Record<EventType, string>)[et];

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* 顶部 spec 锚点 + 站点过滤 */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={12}>
          <Col flex="100px"><strong>📊 流量分析</strong></Col>
          <Col flex="auto">
            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
              spec § 4.9 · 落 saas_analytics · 写路径无回查无 UPDATE · 维表 cache+ON DUPLICATE 解析 · 接口 POST /idp/events（≤50 events/请求）
            </span>
            <br />
            <span style={{ fontSize: 11, color: '#b26a00' }}>
              灰度回滚：URL 追加 ?traffic_version=v1 可秒切旧流量页；当前 v2 站点 query 会同步 webKey。
            </span>
          </Col>
          <Col>
            <Tooltip title={apiState.error || (usingApi ? '当前页面数据来自 gateway /idp/analytics/*' : 'API 暂不可用时自动回落到 demo mock 数据')}>
              <Tag color={apiState.loading ? 'processing' : usingApi ? 'success' : 'warning'}>
                {apiState.loading ? '加载中' : usingApi ? '真实 API' : 'Mock 兜底'}
              </Tag>
            </Tooltip>
          </Col>
          <Col>
            <Button size="small" onClick={() => setApiRefreshKey(v => v + 1)}>刷新</Button>
          </Col>
          <Col>
            <span style={{ marginRight: 4 }}>站点:</span>
            <Select
              value={filterSite || undefined}
              onChange={setFilterSite}
              style={{ width: 120 }}
              size="small"
              loading={siteAccessLoading}
              disabled={siteAccessLoading || !hasSiteAccess}
              placeholder="无权限"
            >
              {siteOptions.length > 1 ? <Select.Option value="all">全部</Select.Option> : null}
              {siteOptions.map(site => (
                <Select.Option key={site.siteId} value={site.siteId}>{site.label}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col>
            <span style={{ marginRight: 4 }}>时间:</span>
            <RangeSelect
              value={dateRange}
              startName="from"
              endName="to"
              isThreeMonth
              onChange={(v: any) => setDateRange(v || { from: Date.now() - 7 * 24 * 3600 * 1000, to: Date.now() })}
            />
          </Col>
        </Row>
      </Card>

      {!hasSiteAccess && !siteAccessLoading ? (
        <Alert
          type="warning"
          showIcon
          message="当前员工没有 FG/ST/BF 独立站渠道权限"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          // ============================================================
          // Tab 1：总览
          // ============================================================
          {
            key: 'overview',
            label: '① 总览',
            children: (
              <>
                <SpecCallout>
                  三层身份持久度（spec §4.9 数字化验收 ② UV 可计算）：
                  <strong> device_id</strong> 落 Shopify <code>_shopify_y</code> cookie（1 年） ·
                  <strong> session_id</strong> 落 <code>_shopify_s</code>（30min 无操作切） ·
                  <strong> customer_id</strong> 登录后才有
                </SpecCallout>
                <BizCallout>
                  每天上班 30 秒看一眼 — DAU 是不是涨 / 跌？登录覆盖率（customer/device 比）够不够 35%（不到说明没人愿意注册）？mobile/desktop 比有没有突变？这页是流量异动的报警面板，不出问题就关掉。
                </BizCallout>

                {/* KPI 7 项 */}
                <Row gutter={12} style={{ marginBottom: 16 }}>
                  <Col span={4}><Card size="small"><Statistic title="device 数（UV）" value={uniqueDevices} prefix={<MobileOutlined />} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="session 数" value={uniqueSessions} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="customer 数（登录）" value={uniqueCustomers} valueStyle={{ color: '#52c41a' }} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="登录覆盖率" value={(customerCoverage * 100).toFixed(1)} suffix="%" valueStyle={{ color: customerCoverage > 0.35 ? '#52c41a' : '#fa8c16' }} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="DAU（today）" value={dauWauMau.dau[dauWauMau.dau.length - 1]?.users || 0} /></Card></Col>
                  <Col span={4}><Card size="small"><Statistic title="MAU（30d）" value={dauWauMau.mau} valueStyle={{ color: '#722ed1' }} /></Card></Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Card title="DAU 14 天趋势" size="small">
                      <div style={{ display: 'flex', alignItems: 'flex-end', height: 140, gap: 4, padding: '8px 0' }}>
                        {dauWauMau.dau.map(d => {
                          const max = Math.max(...dauWauMau.dau.map(x => x.users), 1);
                          const h = (d.users / max) * 100;
                          return (
                            <Tooltip key={d.date} title={`${d.date}: ${d.users}`}>
                              <div style={{ flex: 1, background: 'linear-gradient(180deg, #1890ff, #69c0ff)', height: `${h}%`, borderRadius: '2px 2px 0 0', minHeight: 4 }} />
                            </Tooltip>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 4, fontSize: 9, color: '#8c8c8c', marginTop: 4 }}>
                        {dauWauMau.dau.map(d => <div key={d.date} style={{ flex: 1, textAlign: 'center' }}>{d.date.slice(5)}</div>)}
                      </div>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card title="设备分布（session 维度）" size="small">
                      {(['mobile', 'desktop', 'tablet'] as DeviceType[]).map(dt => {
                        const total = Object.values(deviceStats).reduce((s, n) => s + n, 0);
                        const pct = total > 0 ? deviceStats[dt] / total * 100 : 0;
                        return (
                          <div key={dt} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span>{DEVICE_ICONS[dt]} {dt}</span>
                              <span>{deviceStats[dt]} ({pct.toFixed(0)}%)</span>
                            </div>
                            <Progress percent={pct} showInfo={false} strokeColor={dt === 'mobile' ? '#1890ff' : dt === 'desktop' ? '#52c41a' : '#fa8c16'} />
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card title="事件类型分布" size="small">
                      {Object.entries(eventTypeStats).sort((a, b) => (b[1] as number) - (a[1] as number)).map(([t, n]) => {
                        const pct = ((n as number) / eventTypeTotal * 100);
                        return (
                          <div key={t} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                            <Tag color={EVENT_TYPE_COLORS[t as EventType]}>{EVENT_TYPE_LABELS[t as EventType]}</Tag>
                            <span>{n} <small style={{ color: '#8c8c8c' }}>({pct.toFixed(1)}%)</small></span>
                          </div>
                        );
                      })}
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 2：会话与路径
          // ============================================================
          {
            key: 'session',
            label: '② 会话与路径',
            children: (
              <>
                <SpecCallout>
                  Sessionization 客户端切（spec §4.9 业界做法）：
                  Shopify <code>_shopify_s</code> 30min 无操作过期；engagement_time_msec 客户端算（GA4 / Snowplow page_ping 心跳）；服务端只 append 不回查
                </SpecCallout>
                <BizCallout>
                  这页解决"用户在站上到底干了啥"。查询条按转化（加购/购买/询单/跳出）+ 时长筛 → 看具体 session 的事件链 → 揭示流失节点。比如<strong>加购但没结账</strong>的 session 都在哪些 PDP 流失？退出页 Top N 直接告诉你哪页该重点优化。
                </BizCallout>

                {/* 会话查询条 */}
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={8} align="middle">
                    <Col flex="240px">
                      <Input.Search
                        size="small"
                        placeholder="device / customer / session id 模糊"
                        value={sessionFilters.keyword}
                        onChange={e => setSessionFilters(f => ({ ...f, keyword: e.target.value }))}
                        allowClear
                      />
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>转化:</span>
                      <Select
                        size="small"
                        mode="multiple"
                        placeholder="任意"
                        style={{ width: 180 }}
                        value={sessionFilters.conversions}
                        onChange={v => setSessionFilters(f => ({ ...f, conversions: v }))}
                        maxTagCount={2}
                      >
                        <Select.Option value="atc">含加购</Select.Option>
                        <Select.Option value="purchase">含购买</Select.Option>
                        <Select.Option value="inquiry">含询单</Select.Option>
                        <Select.Option value="bounce">跳出(PV=1)</Select.Option>
                      </Select>
                    </Col>
                    <Col>
                      <span style={{ fontSize: 11, marginRight: 4 }}>设备:</span>
                      <Select
                        size="small"
                        mode="multiple"
                        placeholder="全部"
                        style={{ width: 160 }}
                        value={sessionFilters.deviceTypes}
                        onChange={v => setSessionFilters(f => ({ ...f, deviceTypes: v }))}
                        maxTagCount={3}
                      >
                        <Select.Option value="mobile">mobile</Select.Option>
                        <Select.Option value="desktop">desktop</Select.Option>
                        <Select.Option value="tablet">tablet</Select.Option>
                      </Select>
                    </Col>
                    <Col>
                      <Button size="small" onClick={() => setShowAdvancedFilter(s => !s)}>
                        {showAdvancedFilter ? '收起' : '高级'}{activeFilterCount > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{activeFilterCount}</Tag>}
                      </Button>
                    </Col>
                    {activeFilterCount > 0 && (
                      <Col>
                        <Button size="small" type="link" onClick={resetSessionFilters}>清空</Button>
                      </Col>
                    )}
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        命中 <strong>{filteredSessions.length}</strong> / 全部 {allSessions.length} 会话
                      </span>
                    </Col>
                  </Row>
                  {showAdvancedFilter && (
                    <Row gutter={8} align="middle" style={{ marginTop: 8 }}>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>入口页:</span>
                        <Select
                          size="small"
                          showSearch
                          style={{ width: 180 }}
                          value={sessionFilters.entryPageId}
                          onChange={v => setSessionFilters(f => ({ ...f, entryPageId: v }))}
                          optionFilterProp="children"
                        >
                          <Select.Option value="all">全部</Select.Option>
                          {idpPages.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>退出页:</span>
                        <Select
                          size="small"
                          showSearch
                          style={{ width: 180 }}
                          value={sessionFilters.exitPageId}
                          onChange={v => setSessionFilters(f => ({ ...f, exitPageId: v }))}
                          optionFilterProp="children"
                        >
                          <Select.Option value="all">全部</Select.Option>
                          {idpPages.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col>
                        <span style={{ fontSize: 11, marginRight: 4 }}>时长:</span>
                        <Select
                          size="small"
                          mode="multiple"
                          placeholder="全部"
                          style={{ width: 240 }}
                          value={sessionFilters.durationBuckets}
                          onChange={v => setSessionFilters(f => ({ ...f, durationBuckets: v }))}
                          maxTagCount={4}
                        >
                          <Select.Option value="lt30s">&lt; 30s</Select.Option>
                          <Select.Option value="30s_5m">30s - 5min</Select.Option>
                          <Select.Option value="5m_30m">5 - 30min</Select.Option>
                          <Select.Option value="gt30m">&gt; 30min</Select.Option>
                        </Select>
                      </Col>
                    </Row>
                  )}
                </Card>

                <Row gutter={12}>
                  {/* 左：session 列表 */}
                  <Col span={7}>
                    <Card size="small" title={`会话列表 (${filteredSessions.length})`} bodyStyle={{ padding: 0, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                      {filteredSessions.slice(0, 50).map(s => (
                        <div
                          key={s.session_id}
                          onClick={() => setSelectedSessionId(s.session_id)}
                          style={{
                            padding: 10, borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                            background: selectedSessionId === s.session_id ? '#e6f7ff' : undefined,
                            borderLeft: selectedSessionId === s.session_id ? '3px solid #1890ff' : '3px solid transparent',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ fontFamily: 'monospace' }}>{s.session_id}</span>
                            <span>{DEVICE_ICONS[s.device_type]} {SITE_NAMES[s.site_id]}</span>
                          </div>
                          <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                            {s.device_id} {s.customer_id ? `· 已登录` : '· 匿名'}
                          </div>
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            <Tag color="blue" style={{ fontSize: 10 }}>{s.page_view_count} PV</Tag>
                            {s.has_atc && <Tag color="orange" style={{ fontSize: 10 }}>加购</Tag>}
                            {s.has_purchase && <Tag color="green" style={{ fontSize: 10 }}>购买</Tag>}
                            {s.has_inquiry && <Tag color="magenta" style={{ fontSize: 10 }}>询单</Tag>}
                          </div>
                          <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 4 }}>
                            <ClockCircleOutlined /> {Math.round(s.duration_ms / 60000)}min · {new Date(s.start_at).toLocaleString('zh-CN')}
                          </div>
                        </div>
                      ))}
                    </Card>
                  </Col>

                  {/* 中：单 session 事件链 */}
                  <Col span={10}>
                    <Card size="small" title={`session 事件链：${selectedSessionId || '未选'}`} style={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
                      {selectedSession ? (
                        <>
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 12 }}>
                            <div>device_id: <code>{selectedSession.device_id}</code></div>
                            <div>customer_id: <code>{selectedSession.customer_id || '(匿名)'}</code></div>
                            <div>device: {selectedSession.device_type} · site: {SITE_NAMES[selectedSession.site_id]}</div>
                            <div>入口: {selectedSession.entry_page_url} · 退出: {selectedSession.exit_page_url}</div>
                          </div>
                          <Timeline mode="left">
                            {selectedSessionEvents.map(e => (
                              <Timeline.Item
                                key={e.id}
                                color={sessionTimelineColor(e.event_type)}
                                label={<span style={{ fontSize: 10 }}>{new Date(e.event_time).toLocaleTimeString('zh-CN')}</span>}
                              >
                                <div style={{ fontSize: 12 }}>
                                  <Tag color={EVENT_TYPE_COLORS[e.event_type]}>{EVENT_TYPE_LABELS[e.event_type]}</Tag>
                                  {e.page_id && (
                                    <span>{idpPages.find(p => p.id === e.page_id)?.title || e.props?.pageUrl || ''}</span>
                                  )}
                                  {e.event_type === 'click' && e.element_id && (
                                    <code style={{ fontSize: 10, marginLeft: 8 }}>
                                      {idpElements.find(el => el.id === e.element_id)?.selector}
                                    </code>
                                  )}
                                  {e.engagement_ms && (
                                    <small style={{ color: '#8c8c8c', marginLeft: 8 }}>{e.engagement_ms}ms 停留</small>
                                  )}
                                  {e.scroll_depth && (
                                    <Tag style={{ fontSize: 10, marginLeft: 8 }}>滚至 {e.scroll_depth}%</Tag>
                                  )}
                                </div>
                              </Timeline.Item>
                            ))}
                          </Timeline>
                        </>
                      ) : <Empty description="左侧选一个会话" />}
                    </Card>
                  </Col>

                  {/* 右：路径分析 + 退出页 + 跨站点 device */}
                  <Col span={7}>
                    <Card size="small" title="路径分析（从 X 页用户去了 Top N）" style={{ marginBottom: 12 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 11, marginRight: 4 }}>起点页:</span>
                        <Select value={selectedFlowPageId} onChange={setSelectedFlowPageId} style={{ width: 200 }} size="small">
                          {idpPages.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
                          ))}
                        </Select>
                      </div>
                      {pathFlow.length === 0 ? <Empty description="无后续路径" /> : pathFlow.map(p => (
                        <div key={p.next_page_id} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span>→ {p.next_title}</span>
                            <span>{p.count} ({(p.pct * 100).toFixed(0)}%)</span>
                          </div>
                          <Progress percent={p.pct * 100} showInfo={false} size="small" />
                        </div>
                      ))}
                    </Card>

                    <Card size="small" title="退出页 Top 8" style={{ marginBottom: 12 }}>
                      {exitPages.map(e => (
                        <div key={e.page_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                          <span>{e.title}</span>
                          <span><strong>{e.exits}</strong> <small style={{ color: '#cf1322' }}>({(e.pct * 100).toFixed(0)}%)</small></span>
                        </div>
                      ))}
                    </Card>

                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 3：漏斗与留存
          // ============================================================
          {
            key: 'funnel',
            label: '③ 路径与留存',
            children: (
              <>
                <SpecCallout>
                  桑基图：起点 → 多步分流可视化（ribbon 宽 = 流量大小）。同 GA4 Explorations Path 范式；按 session 路径聚合，相同步骤同页 group by。留存：cohort_date 是 device 首次访问日
                </SpecCallout>
                <BizCallout>
                  桑基看"用户怎么走的"——同一个起点（比如哥特 collection 页）分流到哪些 PDP？多少最终 atc？多少直接退出？比直线漏斗多看到"<strong>分支</strong>"和"<strong>回流</strong>"。下方 Cohort 留存看不同周新用户的复访率，d7 留存高 = 站黏性高，是品牌力的硬指标。
                </BizCallout>

                <Card size="small" style={{ marginBottom: 12 }} title="用户路径桑基图（起点 → 多步分流）" extra={
                  <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                    选起点 → 看后续 {sankeyMaxDepth} 步分流。ribbon 宽 = 流量；hover 看具体数；流向"退出"=该步用户离开
                  </span>
                }>
                  {/* 起点选 + 深度选 */}
                  <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
                    <Col><span style={{ fontSize: 11 }}>起点:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        value={sankeyStart}
                        onChange={setSankeyStart}
                        style={{ width: 240 }}
                        showSearch
                        optionFilterProp="children"
                      >
                        <Select.OptGroup label="按入口类型">
                          <Select.Option value="any">🌐 任意入口</Select.Option>
                          <Select.Option value="type:HOMEPAGE">🏠 任意首页入</Select.Option>
                          <Select.Option value="type:COLLECTION">📑 任意 collection 入</Select.Option>
                          <Select.Option value="type:PDP">💍 任意 PDP 入（直达）</Select.Option>
                          <Select.Option value="type:BLOG">📝 任意博客入</Select.Option>
                        </Select.OptGroup>
                        <Select.OptGroup label="按具体页面">
                          {idpPages.map(p => (
                            <Select.Option key={p.id} value={`page:${p.id}`}>{p.title}</Select.Option>
                          ))}
                        </Select.OptGroup>
                      </Select>
                    </Col>
                    <Col style={{ marginLeft: 16 }}><span style={{ fontSize: 11 }}>深度:</span></Col>
                    <Col>
                      <Select
                        size="small"
                        value={sankeyMaxDepth}
                        onChange={setSankeyMaxDepth}
                        style={{ width: 100 }}
                      >
                        {[3, 4, 5, 6, 7].map(n => <Select.Option key={n} value={n}>{n} 步</Select.Option>)}
                      </Select>
                    </Col>
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        命中 <strong>{sankeyData.totalStart}</strong> 个 session 起点
                      </span>
                    </Col>
                  </Row>

                  {/* 桑基图 */}
                  <div style={{ overflowX: 'auto' }}>
                    <SankeyChart data={sankeyData} width={Math.max(900, sankeyMaxDepth * 200)} height={460} />
                  </div>

                  {/* 节点类型 legend */}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#595959', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      { type: 'HOMEPAGE', label: '首页', color: '#1890ff' },
                      { type: 'COLLECTION', label: 'Collection', color: '#13c2c2' },
                      { type: 'PDP', label: 'PDP', color: '#722ed1' },
                      { type: 'CART', label: '购物车', color: '#fa8c16' },
                      { type: 'CHECKOUT', label: '结账', color: '#eb2f96' },
                      { type: 'ACTION', label: '动作(加购/购/询)', color: '#fa541c' },
                      { type: 'EXIT', label: '退出', color: '#8c8c8c' },
                    ].map(t => (
                      <span key={t.type}>
                        <span style={{ display: 'inline-block', width: 12, height: 8, background: t.color, marginRight: 4, verticalAlign: 'middle' }} />
                        {t.label}
                      </span>
                    ))}
                  </div>
                </Card>

                <Row gutter={12}>
                  <Col span={12}>
                    <Card size="small" title="多步自定义漏斗（直线，按 device 去重）" extra={
                      <Button size="small" onClick={() => setFunnelSteps(DEFAULT_FUNNEL)}>重置</Button>
                    }>
                      {calculatedFunnel.map((s, i) => {
                        const widthPct = (s.user_count / funnelMax) * 100;
                        return (
                          <div key={s.step_no} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span>Step {s.step_no}：{s.name}</span>
                              <span>
                                <strong>{s.user_count}</strong> users
                                {i > 0 && (
                                  <Tag color={s.drop_off_pct > 0.5 ? 'red' : s.drop_off_pct > 0.3 ? 'orange' : 'green'} style={{ marginLeft: 8 }}>
                                    -{(s.drop_off_pct * 100).toFixed(1)}%
                                  </Tag>
                                )}
                              </span>
                            </div>
                            <div style={{ position: 'relative', height: 24, background: '#fafafa', borderRadius: 3, marginTop: 4 }}>
                              <div style={{
                                width: `${widthPct}%`, height: '100%',
                                background: `linear-gradient(90deg, #1890ff, ${i === 0 ? '#1890ff' : i === calculatedFunnel.length - 1 ? '#52c41a' : '#69c0ff'})`,
                                borderRadius: 3, transition: 'width 0.3s',
                              }} />
                            </div>
                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                              match: {s.match.event_type ? `event=${s.match.event_type}` : `page=${s.match.page_type}`}
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 8 }}>
                        端到端转化率: <strong>{(calculatedFunnel.length ? calculatedFunnel[calculatedFunnel.length - 1].user_count / (calculatedFunnel[0].user_count || 1) * 100 : 0).toFixed(2)}%</strong>
                      </div>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card size="small" title="Cohort 留存矩阵（device_id 维度）">
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <th style={{ padding: 4, textAlign: 'left' }}>Cohort（首次访问周）</th>
                            <th style={{ padding: 4, textAlign: 'right' }}>size</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d1</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d3</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d7</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d14</th>
                            <th style={{ padding: 4, textAlign: 'center' }}>d30</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cohorts.map(c => {
                            const cell = (v: number) => {
                              const intensity = v;
                              const bg = `rgba(24, 144, 255, ${intensity})`;
                              const fg = intensity > 0.15 ? '#fff' : '#262626';
                              return <td style={{ padding: 4, textAlign: 'center', background: bg, color: fg, fontSize: 10, border: '1px solid #fff' }}>{(v * 100).toFixed(1)}%</td>;
                            };
                            return (
                              <tr key={c.cohort_date}>
                                <td style={{ padding: 4 }}>{c.cohort_date}</td>
                                <td style={{ padding: 4, textAlign: 'right' }}>{c.cohort_size}</td>
                                {cell(c.retention.d1)}
                                {cell(c.retention.d3)}
                                {cell(c.retention.d7)}
                                {cell(c.retention.d14)}
                                {cell(c.retention.d30)}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
                        珠宝独立站 d1 留存基线 ~30%，d7 ~10%；本图色深 = 留存率高
                      </div>
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },

          // ============================================================
          // Tab 4：热区
          // ============================================================
          {
            key: 'heatmap',
            label: '④ 热区',
            children: (
              <>
                <SpecCallout>
                  click 全采（spec 决策 ③ Clarity 模式）：每 click 记 element_selector + x/y%（设备无关）+ viewport_w 桶 → 维表 ID 解析 + ON DUPLICATE 写 idp_event。<strong>生产部署 = postMessage 协议</strong>: 后台 iframe 加载真实独立站页面 → 父窗口 postMessage 探针激活指令 → SDK querySelector 返回元素坐标 + 叠热区图层（不影响普通用户）。无真实 pageUrl/selector 时回落 PDP 视觉 mock。
                </SpecCallout>
                <BizCallout>
                  热区告诉你"<strong>用户眼睛在哪里</strong>"。哪些按钮被狂点 → 是不是真要点的（如错把图当按钮）？加购按钮点击多但 atc 数少 → 按钮文案 / 颜色 / 价格区不对劲？scroll 完读率低的 PDP → 描述太长用户跑了。所有这些发现都可直接进 A/B 实验验证。
                </BizCallout>

                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={16}>
                    <Card size="small" title={<span><DesktopOutlined /> PDP 真实页面热点叠加</span>} extra={
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>hover 热点看 selector / 7d click 数 / 占比</span>
                    }>
                      {apiState.heatmap?.pageUrl && viewportHeatmap.desktop?.some(point => (point as HeatmapOverlayPoint).selector) ? (
                        <IframeHeatmapProbe
                          pageUrl={apiState.heatmap.pageUrl}
                          points={viewportHeatmap.desktop as HeatmapOverlayPoint[]}
                        />
                      ) : (
                        <PdpHeatmapMock />
                      )}
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" title="viewport 桶热区分层" style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8 }}>
                        按 viewport_w 桶（mobile 375 / desktop 1280+）独立聚合热区，避免移动端 click 拉偏桌面位置
                      </div>
                      {(['mobile', 'desktop', 'tablet'] as DeviceType[]).map(dt => (
                        <div key={dt} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                            <span>{DEVICE_ICONS[dt]} {dt}</span>
                            <span><strong>{viewportHeatmap[dt]?.length || 0}</strong> 桶</span>
                          </div>
                          <div style={{
                            position: 'relative', height: 80,
                            background: 'linear-gradient(180deg, #fafafa, #f0f0f0)',
                            border: '1px solid #d9d9d9', borderRadius: 3, overflow: 'hidden',
                          }}>
                            {viewportHeatmap[dt]?.map((h, i) => (
                              <div key={i} style={{
                                position: 'absolute', left: `${h.x}%`, top: `${h.y}%`,
                                width: 14 + h.intensity * 18, height: 14 + h.intensity * 18,
                                transform: 'translate(-50%, -50%)',
                                background: `radial-gradient(circle, rgba(255, 0, 0, ${h.intensity * 0.55}) 0%, rgba(255, 200, 0, ${h.intensity * 0.25}) 50%, transparent 80%)`,
                                borderRadius: '50%',
                              }} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </Card>
                  </Col>
                </Row>

                <Card size="small" title="Scroll 完读率（每页用户读到 100% 的比例）">
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8 }}>
                    业务含义：完读率低 = 用户上来就跑 / 中段流失；A/B 改完率走势可验证"短描述 vs 长描述"哪种被看完
                  </div>
                  <Table
                    size="small"
                    pagination={{ pageSize: 10 }}
                    rowKey="page_id"
                    dataSource={scrollReadRate}
                    columns={[
                      { title: '页面', dataIndex: 'title' },
                      { title: 'URL', dataIndex: 'url', render: u => <code style={{ fontSize: 10 }}>{u}</code> },
                      {
                        title: '完读率（100%）', dataIndex: 'reached_100_pct',
                        render: v => (
                          <span>
                            <Progress percent={v * 100} size="small" style={{ width: 100, marginRight: 8 }} strokeColor={v > 0.5 ? '#52c41a' : v > 0.3 ? '#fa8c16' : '#cf1322'} />
                            <strong>{(v * 100).toFixed(1)}%</strong>
                          </span>
                        ),
                      },
                      {
                        title: '顶部弃读',
                        dataIndex: 'topBounce_pct',
                        render: v => <Tag color={v > 0.4 ? 'red' : v > 0.2 ? 'orange' : 'green'}>{(v * 100).toFixed(1)}%</Tag>,
                      },
                    ]}
                  />
                </Card>
              </>
            ),
          },

          // ============================================================
          // Tab 5：实时事件流
          // ============================================================
          {
            key: 'realtime',
            label: '⑤ 实时事件流',
            children: (
              <>
                <SpecCallout>
                  实时事件流由 collector 立即 200 异步落库，前端 sendBeacon 批量 ≤50 events/请求；engagement_ms 客户端算（page_ping 心跳）
                </SpecCallout>
                <BizCallout>
                  这页是埋点 SDK 健康监测——上线新版 theme / 改了埋点代码后，看实时流确认事件还在正常进来。engagement_ms 直方图揭示用户停留时长分布，&lt;2s 跳出占比突增 = 站速或首屏视觉出问题；&gt;30s 集中 = 内容引人。
                </BizCallout>

                <Row gutter={12}>
                  <Col span={16}>
                    <Card size="small" title="最近 60 条事件（按 ts desc）" extra={
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        <EyeOutlined /> {usingApi ? '来自 gateway realtime endpoint · 10s 自动刷新' : 'mock 快照'}
                      </span>
                    } style={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <th style={{ padding: 4, textAlign: 'left' }}>ts</th>
                            <th style={{ padding: 4 }}>type</th>
                            <th style={{ padding: 4 }}>device_id</th>
                            <th style={{ padding: 4 }}>session</th>
                            <th style={{ padding: 4 }}>page</th>
                            <th style={{ padding: 4 }}>device</th>
                            <th style={{ padding: 4 }}>site</th>
                          </tr>
                        </thead>
                        <tbody>
                          {realtimeEvents.map(e => (
                            <tr key={e.id} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                              <td style={{ padding: 4, fontFamily: 'monospace', fontSize: 10 }}>
                                {new Date(e.event_time).toLocaleTimeString('zh-CN')}
                              </td>
                              <td style={{ padding: 4 }}><Tag color={EVENT_TYPE_COLORS[e.event_type]} style={{ fontSize: 10 }}>{e.event_type}</Tag></td>
                              <td style={{ padding: 4, fontFamily: 'monospace', fontSize: 10 }}>{e.device_id}</td>
                              <td style={{ padding: 4, fontFamily: 'monospace', fontSize: 10 }}>{(e.session_id || '').slice(0, 12)}</td>
                              <td style={{ padding: 4, fontSize: 10 }}>{e.page_id ? (idpPages.find(p => p.id === e.page_id)?.title || e.props?.pageUrl) : (e.props?.pageUrl || '-')}</td>
                              <td style={{ padding: 4 }}>{DEVICE_ICONS[e.device_type]}</td>
                              <td style={{ padding: 4 }}>{SITE_NAMES[e.site_id]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </Col>

                  <Col span={8}>
                    <Card size="small" title="engagement_ms 直方图" style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginBottom: 8 }}>
                        客户端 page_ping 心跳累加（spec 业界做法 / Snowplow）
                      </div>
                      {engagementDist.map(b => {
                        const maxCount = Math.max(...engagementDist.map(x => x.count), 1);
                        return (
                          <div key={b.bucket} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                              <span>{b.bucket}</span>
                              <span>{b.count} <small style={{ color: '#8c8c8c' }}>({(b.pct * 100).toFixed(1)}%)</small></span>
                            </div>
                            <Progress percent={(b.count / maxCount) * 100} showInfo={false} size="small" />
                          </div>
                        );
                      })}
                    </Card>

                    <Card size="small" title="事件类型分布（实时窗口）">
                      {Object.entries(eventTypeStats).sort((a, b) => (b[1] as number) - (a[1] as number)).map(([t, n]) => (
                        <div key={t} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                          <Tag color={EVENT_TYPE_COLORS[t as EventType]}>{t}</Tag>
                          <span>{n}</span>
                        </div>
                      ))}
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TrafficAnalysis;
